import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Pagination,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import CancelIcon from '@mui/icons-material/Cancel';
import PaymentIcon from '@mui/icons-material/Payment';
import { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, type BookingResponse, type BookingStatus } from '@ota/api-client';
import { NumericField, OtaDatePicker, OtaFooter, formatDate, formatDateTime } from '@ota/design-system';
import {
  authStore, cancelBooking, cancelBookingPublic, fetchModifyPreview, fetchModifyPreviewPublic,
  fetchMyBookings, fetchMyBookingsPublic, fetchRefundPreview, fetchRefundPreviewPublic,
  modifyBooking, modifyBookingPublic,
} from '../api';
import { PublicHeader } from '../components/PublicHeader';
import { useRazorpayCheckout } from '../hooks/useRazorpayCheckout';

/**
 * A booking is "resumable" if the guest started a pay-now checkout, didn't
 * complete it, and the TTL window hasn't expired yet. The 15-minute TTL
 * mirrors {@code ota.booking.pending-ttl-minutes} on the backend — if we
 * showed the button beyond that, the backend's scheduled job would have
 * already flipped the booking to PAYMENT_FAILED.
 */
const PENDING_TTL_MINUTES = 15;
function isResumable(b: BookingResponse): boolean {
  if (b.status !== 'PENDING' || b.payAtHotel) return false;
  const createdMs = new Date(b.createdAt).getTime();
  return Date.now() - createdMs < PENDING_TTL_MINUTES * 60 * 1000;
}

const STATUS_COLOR: Record<BookingStatus, 'default' | 'success' | 'error' | 'warning' | 'info'> = {
  PENDING: 'warning',
  CONFIRMED: 'success',
  CANCELLED: 'default',
  COMPLETED: 'info',
  PAYMENT_FAILED: 'error',
};

export function MyBookingsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = authStore.get();
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));

  // Opened from the "View my bookings" link in the confirmation email —
  // works without a login session, authorized by the token instead. See
  // PublicBookingController on the backend. Any query/mutation below that
  // touches a booking branches on `token` to hit the /public/bookings/*
  // endpoints instead of the session-authenticated ones.
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [page, setPage] = useState(0);
  const PAGE_SIZE = 10;
  // Shared Razorpay flow — used by the "Complete payment" button below.
  const razorpay = useRazorpayCheckout();
  const [resumeError, setResumeError] = useState<string | null>(null);

  const handleResumePayment = (b: BookingResponse) => {
    setResumeError(null);
    void razorpay.start(
      b,
      {
        name: session?.user.fullName,
        email: session?.user.email ?? b.guestEmail,
        contact: session?.user.phone ?? undefined,
      },
      {
        onSuccess: () => {
          // Refetch the list so the booking renders as CONFIRMED + PAID
          // without the resume button.
          queryClient.invalidateQueries({ queryKey: ['my-bookings'] });
        },
        onFailure: (msg) => setResumeError(msg),
        onDismiss: () => {
          // Modal closed without paying — leave the list as-is. Next refetch
          // (or the backend TTL job at the 15-min mark) will pick it up.
        },
      },
    );
  };

  const list = useQuery({
    queryKey: ['my-bookings', page, token],
    queryFn: () => (token ? fetchMyBookingsPublic(token, page, PAGE_SIZE) : fetchMyBookings(page, PAGE_SIZE)),
    enabled: !!session || !!token,
  });

  const [modifyOpen, setModifyOpen] = useState(false);
  const [editing, setEditing] = useState<BookingResponse | null>(null);
  const [newCheckIn, setNewCheckIn] = useState('');
  const [newCheckOut, setNewCheckOut] = useState('');
  const [newGuests, setNewGuests] = useState('1');
  const [newRooms, setNewRooms] = useState('1');

  const openModify = (b: BookingResponse) => {
    setEditing(b);
    setNewCheckIn(b.checkIn);
    setNewCheckOut(b.checkOut);
    setNewGuests(String(b.guestsCount));
    setNewRooms(String(b.rooms[0]?.roomsCount ?? 1));
    setModifyOpen(true);
  };

  // Cancel flow: open a dialog, fetch the refund preview, then confirm.
  const [cancelTarget, setCancelTarget] = useState<BookingResponse | null>(null);
  const refundPreview = useQuery({
    queryKey: ['refund-preview', cancelTarget?.id, token],
    queryFn: () => (token
      ? fetchRefundPreviewPublic(cancelTarget!.id, token)
      : fetchRefundPreview(cancelTarget!.id)),
    enabled: !!cancelTarget,
  });

  const cancelMutation = useMutation({
    mutationFn: (id: number) => (token ? cancelBookingPublic(id, token) : cancelBooking(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-bookings'] });
      setCancelTarget(null);
    },
  });

  // Shared by the price preview (below) and the actual save, so they can
  // never drift out of sync with each other.
  const buildModifyRequest = () => ({
    checkIn: newCheckIn !== editing!.checkIn ? newCheckIn : undefined,
    checkOut: newCheckOut !== editing!.checkOut ? newCheckOut : undefined,
    guestsCount: Number(newGuests) !== editing!.guestsCount ? Number(newGuests) : undefined,
    roomsCount: Number(newRooms) !== (editing!.rooms[0]?.roomsCount ?? 1) ? Number(newRooms) : undefined,
  });

  const datesValid = !!newCheckIn && !!newCheckOut && newCheckOut > newCheckIn;

  // Live price-impact preview — refetches whenever the form values change.
  // Tells us (a) what the new total would be and (b) whether the change is
  // blocked outright because the booking is already paid and the total
  // would change (see PriceChangeQuote on the backend).
  const modifyPreview = useQuery({
    queryKey: ['modify-preview', editing?.id, token, newCheckIn, newCheckOut, newGuests, newRooms],
    queryFn: () => {
      const req = buildModifyRequest();
      return token
        ? fetchModifyPreviewPublic(editing!.id, req, token)
        : fetchModifyPreview(editing!.id, req);
    },
    enabled: modifyOpen && !!editing && datesValid,
  });

  const modifyMutation = useMutation({
    mutationFn: () => {
      const req = buildModifyRequest();
      return token ? modifyBookingPublic(editing!.id, req, token) : modifyBooking(editing!.id, req);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-bookings'] });
      setModifyOpen(false);
    },
  });

  // Without a session, the page only works with a valid link token — otherwise send to login.
  if (!session && !token) return <Navigate to="/login?returnTo=%2Fmy-bookings" replace />;

  // `checkIn` is a yyyy-MM-dd string, so a plain string comparison against
  // today's ISO date sorts correctly without needing a date-parsing library.
  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PublicHeader />
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 }, flex: 1 }}>
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Typography variant="overline" color="secondary.main">My account</Typography>
          <Typography variant="h3">My bookings</Typography>
          <Typography variant="body2" color="text.secondary">
            View, modify, or cancel any of your bookings. Modifications can change dates, guests, and
            rooms — to switch room category you'll need to cancel and rebook.
          </Typography>
        </Stack>

        {token && !session && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You're viewing this via your booking link — no sign-in needed. This link stays valid for a
            while, so feel free to bookmark it or come back later.
          </Alert>
        )}

        {list.isLoading && <CircularProgress />}
        {list.isError && (
          <Alert severity="error">
            {list.error instanceof ApiError
              ? list.error.body?.message ?? "Couldn't load your bookings."
              : "Couldn't load your bookings."}
          </Alert>
        )}
        {resumeError && (
          <Alert severity="error" onClose={() => setResumeError(null)} sx={{ mb: 2 }}>
            {resumeError}
          </Alert>
        )}
        {list.data?.page.totalElements === 0 && (
          <Alert severity="info">
            You don't have any bookings yet.{' '}
            <Button onClick={() => navigate('/')} size="small">Browse hotels</Button>
          </Alert>
        )}

        <Stack spacing={2}>
          {(list.data?.content ?? []).map((b) => {
            // Hide Modify/Cancel once the stay is over — either the backend has
            // already flipped the status, or (in the gap before that job runs)
            // the check-in date has simply passed. `checkIn` is a yyyy-MM-dd
            // string, so a plain string comparison against today's ISO date
            // sorts correctly without needing a date-parsing library.
            const isPast = b.status === 'COMPLETED' || b.status === 'CANCELLED' || b.checkIn < todayIso;
            // Mirrors BookingService's `alreadyPaid` check (PAID or PARTIAL_REFUND —
            // there's captured money on the booking either way). Rather than let the
            // guest open the Modify dialog and then hit a blocked-price message for
            // every paid booking, we skip the dialog entirely: no self-service change
            // is available once money has been captured, so there's nothing to show.
            const isPaid = b.paymentStatus === 'PAID' || b.paymentStatus === 'PARTIAL_REFUND';
            return (
              <Card key={b.id} sx={{ opacity: isPast ? 0.85 : 1 }}>
                <CardContent>
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                    <Stack flex={1} spacing={1}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Typography variant="h5">{b.propertyName}</Typography>
                        <Chip label={b.status} size="small" color={STATUS_COLOR[b.status]} />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        Code {b.bookingCode} · Booked {formatDateTime(b.createdAt)}
                      </Typography>
                      <Stack direction="row" spacing={3} sx={{ mt: 1, color: 'text.secondary' }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <EventIcon fontSize="small" />
                          <Typography variant="body2">{formatDate(b.checkIn)} → {formatDate(b.checkOut)}</Typography>
                        </Stack>
                        <Typography variant="body2">{b.nightsCount} nights · {b.guestsCount} guests</Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {b.rooms.map((r) => `${r.roomsCount} × ${r.name}`).join(', ')}
                      </Typography>
                    </Stack>

                    <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }} spacing={1}>
                      <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
                        <Typography variant="caption" color="text.secondary">Total</Typography>
                        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 700 }}>
                          ₹{Number(b.totalAmount).toLocaleString('en-IN')}
                        </Typography>
                        <Chip label={b.paymentStatus} size="small" variant="outlined" />
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          onClick={() => navigate(
                            token ? `/my-bookings/${b.id}?token=${encodeURIComponent(token)}` : `/my-bookings/${b.id}`,
                          )}
                        >
                          View details
                        </Button>
                      </Stack>
                      {/* Resume payment — only for PENDING pay-now bookings within
                          the TTL window. Renders above Modify/Cancel because completing
                          the payment is the action the guest most likely wants. */}
                      {isResumable(b) && (
                        <Button
                          size="small"
                          variant="contained"
                          color="secondary"
                          startIcon={<PaymentIcon />}
                          disabled={razorpay.busy}
                          onClick={() => handleResumePayment(b)}
                        >
                          {razorpay.busy ? 'Opening…' : 'Complete payment'}
                        </Button>
                      )}
                      {!isPast && (
                        <Stack direction="row" spacing={1}>
                          {!isPaid && (
                            <Button size="small" startIcon={<EditCalendarIcon />} onClick={() => openModify(b)}>
                              Modify
                            </Button>
                          )}
                          <Button
                            size="small" color="error" startIcon={<CancelIcon />}
                            onClick={() => setCancelTarget(b)}
                          >
                            Cancel
                          </Button>
                        </Stack>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>

        {list.data && list.data.page.totalPages > 1 && (
          <Stack alignItems="center" sx={{ pt: 3 }}>
            <Pagination
              count={list.data.page.totalPages}
              page={page + 1}
              onChange={(_, p) => setPage(p - 1)}
              color="primary"
            />
          </Stack>
        )}
      </Container>

      <Dialog open={modifyOpen} onClose={() => setModifyOpen(false)} maxWidth="sm" fullWidth fullScreen={fullScreenDialog}>
        <DialogTitle>Modify booking {editing?.bookingCode}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 1 }}>
              You can change dates, guest count, and room count. To change the room category,
              cancel this booking and rebook.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <OtaDatePicker label="Check-in" fullWidth
                  value={newCheckIn} onChange={setNewCheckIn} />
              </Grid>
              <Grid item xs={6}>
                <OtaDatePicker label="Check-out" fullWidth
                  value={newCheckOut} onChange={setNewCheckOut} />
              </Grid>
              <Grid item xs={6}>
                <NumericField label="Guests" fullWidth
                  value={newGuests}
                  onChange={(e) => setNewGuests(e.target.value)}
                  onBlur={() => setNewGuests((v) => String(Math.max(1, Number(v) || 1)))} />
              </Grid>
              <Grid item xs={6}>
                <NumericField label="Rooms" fullWidth
                  value={newRooms}
                  onChange={(e) => setNewRooms(e.target.value)}
                  onBlur={() => setNewRooms((v) => String(Math.max(1, Number(v) || 1)))} />
              </Grid>
            </Grid>
            {newCheckIn && newCheckOut && newCheckOut <= newCheckIn && (
              <Alert severity="warning">Check-out date must be after check-in date.</Alert>
            )}

            {datesValid && modifyPreview.isLoading && (
              <Typography variant="caption" color="text.secondary">Checking price impact…</Typography>
            )}
            {datesValid && modifyPreview.data && modifyPreview.data.blocked && (
              <Alert severity="warning">{modifyPreview.data.summary}</Alert>
            )}
            {datesValid && modifyPreview.data && !modifyPreview.data.blocked && modifyPreview.data.priceChanged && (
              <Alert severity="info">{modifyPreview.data.summary}</Alert>
            )}

            {modifyMutation.isError && (
              <Alert severity="error">
                {modifyMutation.error instanceof ApiError
                  ? modifyMutation.error.body?.message ?? modifyMutation.error.message
                  : 'Modification failed'}
              </Alert>
            )}
            <Divider />
            <Typography variant="caption" color="text.secondary">
              Server will recalculate the total price based on the new dates and any active special rates.
              If this booking is already paid and the price would change, the change is blocked — cancel this
              booking separately (see the refund preview) and make a new booking for the new dates instead.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModifyOpen(false)}>Close</Button>
          <Button
            variant="contained" color="secondary"
            disabled={
              modifyMutation.isPending
              || !datesValid
              || modifyPreview.isLoading
              || modifyPreview.data?.blocked === true
            }
            onClick={() => modifyMutation.mutate()}
          >
            {modifyMutation.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel + refund-preview dialog */}
      <Dialog open={!!cancelTarget} onClose={() => setCancelTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel Booking id {cancelTarget?.bookingCode}?</DialogTitle>
        <DialogContent dividers>
          {refundPreview.isLoading && <CircularProgress size={24} />}
          {refundPreview.data && (
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                {refundPreview.data.policyName}
              </Typography>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Paid</Typography>
                <Typography variant="body2">₹{Number(refundPreview.data.paidAmount).toLocaleString('en-IN')}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography variant="subtitle2">Refund ({Number(refundPreview.data.refundPercent)}%)</Typography>
                <Typography variant="h6" sx={{ color: refundPreview.data.refundable ? 'success.main' : 'text.secondary' }}>
                  ₹{Number(refundPreview.data.refundAmount).toLocaleString('en-IN')}
                </Typography>
              </Stack>
              {!refundPreview.data.refundable && (
                <Alert severity="warning">No refund applies at this time per the cancellation policy.</Alert>
              )}
              <Typography variant="caption" color="text.secondary">
                {refundPreview.data.summary}
              </Typography>
              {cancelMutation.isError && (
                <Alert severity="error">
                  {cancelMutation.error instanceof ApiError
                    ? cancelMutation.error.body?.message ?? cancelMutation.error.message
                    : 'Cancellation failed'}
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelTarget(null)}>Keep booking</Button>
          <Button
            variant="contained" color="error"
            disabled={cancelMutation.isPending || refundPreview.isLoading}
            onClick={() => cancelTarget && cancelMutation.mutate(cancelTarget.id)}
          >
            {cancelMutation.isPending ? 'Cancelling…' : 'Confirm cancellation'}
          </Button>
        </DialogActions>
      </Dialog>

      <OtaFooter />
    </Box>
  );
}
